/**
 * Quiz Map Component (V2)
 *
 * Simplified Leaflet map for quiz interaction.
 * Shows GeoJSON features with click handling.
 */

import { useEffect, useRef, useMemo, useCallback, useState, memo } from "react";
import { MapContainer, TileLayer, GeoJSON } from "react-leaflet";
import type { Feature, GeoJsonProperties, Geometry, Position } from "geojson";
import type { PathOptions, CircleMarkerOptions } from "leaflet";
import L from "leaflet";
import polylabel from "polylabel";
import "leaflet/dist/leaflet.css";
import type { QuizFeature, QuizAttemptRecord } from "@/stores/useQuizStore";
import { useQuizStore, selectConfig, selectMapSettings, selectMode } from "@/stores/useQuizStore";
import { OfflineBasemap } from "@/components/shared/OfflineBasemap";
import { isOnline, setupOnlineOfflineListeners } from "@/utils/networkStatus";

interface QuizMapProps {
  features: QuizFeature[];
  onFeatureClick: (featureId: string) => void;
  attempts: QuizAttemptRecord[];
  showCompletionLabels?: boolean;
}

// Color constants
const COLOR_DEFAULT = "#3b82f6"; // Blue-500 (unanswered)
const COLOR_CORRECT = "#22c55e"; // Green-500 (correct answer)
const COLOR_INCORRECT = "#ef4444"; // Red-500 (all attempts exhausted)
const COLOR_PARTIAL = "#f97316"; // Orange-500 (incorrect but can retry)
const COLOR_BORDER = "#ffffff"; // White border

type AttemptStatus = {
  status: "correct" | "incorrect" | "partial";
  attemptsUsed: number;
  maxAttempts: number;
};

function QuizMapComponent({ features, onFeatureClick, attempts, showCompletionLabels = false }: QuizMapProps) {
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.GeoJSON | null>(null);
  const hasFitBounds = useRef(false); // Track if we've fitted bounds already
  const [mapReady, setMapReady] = useState(false); // Track if map is ready
  const renderCount = useRef(0);
  renderCount.current++;

  // Label markers (only in complete mode)
  const labelMarkersRef = useRef<Map<string, L.Marker>>(new Map());

  // Use Canvas renderer for better performance with many features
  const canvasRenderer = useRef<L.Canvas | null>(null);
  if (!canvasRenderer.current) {
    canvasRenderer.current = L.canvas({ padding: 0.5 });
  }

  // Get quiz config for maxAttemptsPerPrompt
  const config = useQuizStore(selectConfig);

  // Get map display settings
  const mapSettings = useQuizStore(selectMapSettings);

  // Get quiz mode (to enable tooltips in complete and setup modes)
  const mode = useQuizStore(selectMode);

  // Online/offline state
  const [online, setOnline] = useState(() => isOnline());

  // Set up online/offline listeners
  useEffect(() => {
    const cleanup = setupOnlineOfflineListeners(
      () => setOnline(true),
      () => setOnline(false)
    );
    return cleanup;
  }, []);

  // Memoize feature status map with attempt tracking
  const featureStatusMap = useMemo(() => {
    const map = new Map<string, AttemptStatus>();

    // Group attempts by promptId to track per-question attempts
    const attemptsByPrompt = new Map<string, QuizAttemptRecord[]>();
    attempts.forEach((attempt) => {
      const existing = attemptsByPrompt.get(attempt.promptId) || [];
      attemptsByPrompt.set(attempt.promptId, [...existing, attempt]);
    });

    const maxAttempts = config?.maxAttemptsPerPrompt ?? 1;

    // Process each question's attempts
    attemptsByPrompt.forEach((promptAttempts) => {
      const lastAttempt = promptAttempts[promptAttempts.length - 1];
      if (!lastAttempt) return;

      const attemptsUsed = promptAttempts.length;
      const isExhausted = attemptsUsed >= maxAttempts;

      if (lastAttempt.status === "correct") {
        // Show the CORRECT answer as green
        map.set(lastAttempt.featureId, {
          status: "correct",
          attemptsUsed,
          maxAttempts,
        });
      } else if (isExhausted) {
        // All attempts exhausted - reveal the CORRECT answer as red
        map.set(lastAttempt.featureId, {
          status: "incorrect",
          attemptsUsed,
          maxAttempts,
        });
      }

      // Also color all CLICKED features orange (for incorrect attempts)
      promptAttempts.forEach((attempt) => {
        if (attempt.status === "incorrect") {
          // Only show orange if not exhausted (to avoid conflicting with red reveal)
          if (!isExhausted) {
            map.set(attempt.clickedFeatureId, {
              status: "partial",
              attemptsUsed,
              maxAttempts,
            });
          }
        }
      });
    });

    return map;
  }, [attempts, config]);

  // Memoize style functions to prevent recreation on every render
  const getPolygonStyle = useCallback((feature: Feature<Geometry, GeoJsonProperties>): PathOptions => {
    const featureId = feature.id ? String(feature.id) : "";
    const attemptStatus = featureStatusMap.get(featureId);

    let fillColor = COLOR_DEFAULT;

    // Apply feedback colors (respect settings only during "playing" mode)
    const shouldHideColors = mode === "playing" && mapSettings.hideIndicatorColors;
    if (attemptStatus && !shouldHideColors) {
      if (attemptStatus.status === "correct") {
        fillColor = COLOR_CORRECT; // Green - correct answer
      } else if (attemptStatus.status === "incorrect") {
        fillColor = COLOR_INCORRECT; // Red - all attempts exhausted
      } else if (attemptStatus.status === "partial") {
        fillColor = COLOR_PARTIAL; // Orange - incorrect but can retry
      }
    }

    // Hide outlines only during "playing" mode
    const shouldHideOutlines = mode === "playing" && mapSettings.hideOutlines;

    return {
      fillColor,
      color: COLOR_BORDER,
      weight: 2,
      opacity: shouldHideOutlines ? 0 : 1,
      fillOpacity: 0.5,
    };
  }, [featureStatusMap, mapSettings, mode]);

  // Memoize point style function
  const getPointStyle = useCallback((feature: Feature<Geometry, GeoJsonProperties>): CircleMarkerOptions => {
    const featureId = feature.id ? String(feature.id) : "";
    const attemptStatus = featureStatusMap.get(featureId);

    let fillColor = COLOR_DEFAULT;

    // Apply feedback colors (respect settings only during "playing" mode)
    const shouldHideColors = mode === "playing" && mapSettings.hideIndicatorColors;
    if (attemptStatus && !shouldHideColors) {
      if (attemptStatus.status === "correct") {
        fillColor = COLOR_CORRECT; // Green - correct answer
      } else if (attemptStatus.status === "incorrect") {
        fillColor = COLOR_INCORRECT; // Red - all attempts exhausted
      } else if (attemptStatus.status === "partial") {
        fillColor = COLOR_PARTIAL; // Orange - incorrect but can retry
      }
    }

    // Hide outlines only during "playing" mode
    const shouldHideOutlines = mode === "playing" && mapSettings.hideOutlines;

    return {
      radius: 8,
      fillColor,
      color: COLOR_BORDER,
      weight: 2,
      opacity: shouldHideOutlines ? 0 : 1,
      fillOpacity: 0.8,
    };
  }, [featureStatusMap, mapSettings, mode]);

  // Memoize event handler to prevent re-binding
  const onEachFeature = useCallback((feature: Feature<Geometry, GeoJsonProperties>, layer: L.Layer) => {
    if (!(layer instanceof L.Path)) {
      return;
    }

    const featureId = feature.id ? String(feature.id) : "";

    // Store the original fill opacity for this layer
    const originalFillOpacity = layer.options.fillOpacity ?? 0.5;

    // Add click handler
    layer.on("click", (e) => {
      L.DomEvent.stopPropagation(e); // Prevent event from bubbling
      onFeatureClick(featureId);
    });

    // Add hover effect (only disable during "playing" mode if setting is enabled)
    const shouldDisableHover = mode === "playing" && mapSettings.disableHover;
    if (!shouldDisableHover) {
      layer.on("mouseover", () => {
        layer.setStyle({
          fillOpacity: 0.8,
        });
      });

      layer.on("mouseout", () => {
        // Restore original fill opacity only
        layer.setStyle({
          fillOpacity: originalFillOpacity,
        });
      });
    }

    // Add tooltips in complete and setup modes to explore properties
    if (mode === "complete" || mode === "setup") {
      const properties = feature.properties ?? {};
      const promptKeys = config?.promptKeys ?? [];

      // Build tooltip HTML showing ALL properties
      const tooltipRows = Object.entries(properties)
        .filter(([_, value]) => value !== null && value !== undefined)
        .map(([key, value]) => {
          const isPrompted = promptKeys.includes(key);
          const bgClass = isPrompted
            ? 'background: linear-gradient(to right, rgba(59, 130, 246, 0.12), rgba(59, 130, 246, 0.06)); border-left: 2px solid rgb(59, 130, 246);'
            : 'background: transparent;';
          const keyClass = isPrompted
            ? 'color: rgb(29, 78, 216); font-weight: 600;'
            : 'color: rgb(100, 116, 139); font-weight: 500;';
          const valueClass = isPrompted
            ? 'color: rgb(30, 64, 175); font-weight: 600;'
            : 'color: rgb(30, 41, 59); font-weight: 500;';

          const displayValue = Array.isArray(value)
            ? value.join(', ')
            : String(value);

          // Escape HTML to prevent XSS
          const escapedKey = key.replace(/</g, '&lt;').replace(/>/g, '&gt;');
          const escapedValue = displayValue.replace(/</g, '&lt;').replace(/>/g, '&gt;');

          return `
            <div style="
              padding: 6px 8px;
              margin: 2px 0;
              border-radius: 6px;
              ${bgClass}
              transition: all 0.15s ease;
            ">
              <div style="display: flex; gap: 10px; align-items: baseline;">
                <span style="
                  font-size: 10px;
                  ${keyClass}
                  flex-shrink: 0;
                  text-transform: uppercase;
                  letter-spacing: 0.03em;
                ">${escapedKey}</span>
                <span style="
                  font-size: 12px;
                  ${valueClass}
                  word-break: break-word;
                  flex: 1;
                  line-height: 1.4;
                ">${escapedValue}</span>
              </div>
            </div>
          `;
        })
        .join('');

      const tooltipHTML = `
        <style>
          .quiz-tooltip-content::-webkit-scrollbar {
            width: 6px;
          }
          .quiz-tooltip-content::-webkit-scrollbar-track {
            background: rgba(0, 0, 0, 0.04);
            border-radius: 3px;
          }
          .quiz-tooltip-content::-webkit-scrollbar-thumb {
            background: rgba(59, 130, 246, 0.3);
            border-radius: 3px;
          }
          .quiz-tooltip-content::-webkit-scrollbar-thumb:hover {
            background: rgba(59, 130, 246, 0.5);
          }
        </style>
        <div class="quiz-tooltip-content" style="
          min-width: 180px;
          max-width: 300px;
          max-height: 340px;
          overflow-x: hidden;
          overflow-y: auto;
          padding: 8px;
          scrollbar-width: thin;
          scrollbar-color: rgba(59, 130, 246, 0.3) rgba(0, 0, 0, 0.04);
          background: linear-gradient(to bottom, rgba(255, 255, 255, 0.98), rgba(249, 250, 251, 0.98));
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05);
        ">
          ${tooltipRows}
          ${Object.keys(properties).length > 8 ? `
            <div style="
              text-align: center;
              font-size: 10px;
              color: rgb(100, 116, 139);
              margin-top: 8px;
              padding-top: 8px;
              border-top: 1px solid rgba(0,0,0,0.06);
              font-weight: 500;
              letter-spacing: 0.01em;
            ">
              ${Object.keys(properties).length} properties total
            </div>
          ` : ''}
        </div>
      `;

      layer.bindTooltip(tooltipHTML, {
        sticky: true,
        direction: 'top',
        opacity: 1,
        className: 'quiz-modern-tooltip',
      });
    }
  }, [onFeatureClick, mapSettings, mode, config]);

  // Memoize pointToLayer to prevent recreation
  const pointToLayer = useCallback((feature: Feature<Geometry, GeoJsonProperties>, latlng: L.LatLng) => {
    return L.circleMarker(latlng, getPointStyle(feature));
  }, [getPointStyle]);

  // Fit map to bounds ONCE when layer is ready and features are loaded
  useEffect(() => {
    if (!mapRef.current || !layerRef.current || features.length === 0 || !mapReady) return;
    if (hasFitBounds.current) return; // Already fitted, don't do it again

    // Use a small timeout to ensure the layer has fully rendered
    const timer = setTimeout(() => {
      if (!layerRef.current || !mapRef.current) return;

      const bounds = layerRef.current.getBounds();

      if (bounds.isValid()) {
        mapRef.current.fitBounds(bounds, {
          padding: [32, 32],
          animate: false, // Disable animation for faster initial render
          duration: 0,
        });
        hasFitBounds.current = true;

        // Force a map invalidateSize and redraw after fitBounds
        // This fixes the "zoom to make it work" issue
        setTimeout(() => {
          if (mapRef.current) {
            mapRef.current.invalidateSize();
            // Force the map to redraw by slightly adjusting the zoom
            const currentZoom = mapRef.current.getZoom();
            mapRef.current.setZoom(currentZoom, { animate: false });
          }
        }, 50);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [features, mapReady]);

  // Reset hasFitBounds when features change (new quiz)
  useEffect(() => {
    hasFitBounds.current = false;
  }, [features.length]);

  // Memoize FeatureCollection to prevent recreation
  const featureCollection = useMemo(() => {
    return {
      type: "FeatureCollection" as const,
      features: features.map((f) => ({
        ...f.feature,
        id: f.id, // Ensure id is set correctly
      })),
    };
  }, [features]);

  // Memoize the style function passed to GeoJSON
  const styleFunction = useCallback((feature?: Feature<Geometry, GeoJsonProperties>) => {
    return feature ? getPolygonStyle(feature) : {};
  }, [getPolygonStyle]);

  // Map ready handler
  const handleMapReady = useCallback(() => {
    setMapReady(true);
  }, []);

  /**
   * Find the optimal label position for a feature using pole of inaccessibility algorithm.
   * This finds the point within the polygon that is furthest from any edge.
   * Falls back to bounds center if the geometry is unsupported.
   */
  const findLabelPosition = useCallback((feature: Feature, bounds: L.LatLngBounds): L.LatLng => {
    const geometry = feature.geometry;

    // Helper to convert GeoJSON coordinates to polylabel format
    // GeoJSON uses [longitude, latitude] which maps to [x, y]
    const convertRing = (ring: Position[]): [number, number][] => {
      return ring.map((pos) => [pos[0] ?? 0, pos[1] ?? 0]);
    };

    try {
      if (geometry.type === "Polygon") {
        // Polygon has rings: exterior ring + holes
        const rings = geometry.coordinates.map(convertRing);
        // Use precision of 0.01 for geographic coordinates
        const pole = polylabel(rings, 0.01);
        // polylabel returns [x, y] (lon, lat), we need [lat, lon] for Leaflet
        return L.latLng(pole[1]!, pole[0]!);
      } else if (geometry.type === "MultiPolygon") {
        // For MultiPolygon, find the pole of the largest polygon by area
        let largestPolygonRings: [number, number][][] | null = null;
        let largestArea = 0;

        geometry.coordinates.forEach((polygonCoords) => {
          // Get the exterior ring (first ring)
          const exteriorRing = polygonCoords[0];
          if (!exteriorRing) return;

          const polygon = convertRing(exteriorRing);
          // Simple area approximation (good enough for comparison)
          const area = Math.abs(
            polygon.reduce((sum, [x, y], i, arr) => {
              const [nextX, nextY] = arr[(i + 1) % arr.length] ?? [0, 0];
              return sum + (x * nextY - nextX * y);
            }, 0) / 2,
          );

          if (area > largestArea) {
            largestArea = area;
            largestPolygonRings = polygonCoords.map(convertRing);
          }
        });

        if (largestPolygonRings) {
          // Use precision of 0.01 for geographic coordinates
          const pole = polylabel(largestPolygonRings, 0.01);
          // polylabel returns [x, y] (lon, lat), we need [lat, lon] for Leaflet
          return L.latLng(pole[1]!, pole[0]!);
        }
      }
    } catch (error) {
      // If polylabel fails for any reason, fall back to center
      console.warn("Polylabel failed, using bounds center:", error);
    }

    // Fallback: use bounds center for other geometry types or if polylabel fails
    return bounds.getCenter();
  }, []);

  // Render labels in complete and setup modes when showCompletionLabels is true
  useEffect(() => {
    if (!mapRef.current || (mode !== "complete" && mode !== "setup") || !mapReady) {
      return;
    }

    // Clear existing labels
    labelMarkersRef.current.forEach((marker) => marker.remove());
    labelMarkersRef.current.clear();

    // Only render if showCompletionLabels is true
    if (!showCompletionLabels) {
      return;
    }

    const promptKeys = config?.promptKeys ?? [];
    if (promptKeys.length === 0) {
      return;
    }

    // Add labels for each feature
    features.forEach((quizFeature) => {
      const feature = quizFeature.feature;
      const properties = feature.properties ?? {};

      // Get label text from first prompt key
      const labelKey = promptKeys[0];
      if (!labelKey) return;

      const value = properties[labelKey];
      const labelText = Array.isArray(value)
        ? value.join(", ")
        : String(value);

      if (!labelText) return;

      // Calculate optimal label position using polylabel (pole of inaccessibility)
      // This positions the label at the point farthest from any border
      const geoJsonLayer = L.geoJSON(feature);
      const bounds = geoJsonLayer.getBounds();
      const labelPosition = findLabelPosition(feature, bounds);

      // Escape HTML to prevent XSS
      const escapedLabel = labelText
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

      // Create label marker with divIcon
      const labelIcon = L.divIcon({
        className: "quiz-label-marker",
        html: `
          <div class="label-content" style="
            background: rgba(255, 255, 255, 0.95);
            border: 1px solid rgba(100, 116, 139, 0.3);
            border-radius: 6px;
            padding: 4px 8px;
            font-size: 11px;
            font-weight: 600;
            color: rgb(15, 23, 42);
            white-space: nowrap;
            text-align: center;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            pointer-events: none;
            user-select: none;
            transform: translate(-50%, -50%);
          ">${escapedLabel}</div>
        `,
        iconSize: undefined,
        iconAnchor: [0, 0],
      });

      const marker = L.marker(labelPosition, {
        icon: labelIcon,
        interactive: false,
      }).addTo(mapRef.current!);

      labelMarkersRef.current.set(quizFeature.id, marker);
    });

    // Cleanup labels when component unmounts or dependencies change
    return () => {
      labelMarkersRef.current.forEach((marker) => marker.remove());
      labelMarkersRef.current.clear();
    };
  }, [mode, showCompletionLabels, features, config, mapReady, findLabelPosition]);

  return (
    <div className="map-container relative w-full h-full">
      <MapContainer
        ref={mapRef}
        center={[39.8283, -98.5795]} // Center of US (will auto-fit to bounds)
        zoom={4}
        style={{ height: "100%", width: "100%" }}
        className="rounded-lg overflow-hidden"
        whenReady={handleMapReady}
      >
        {/* Online: Use voyager tiles (same as ExplorationMap) for visual consistency */}
        {online && (
          <>
            {/* Base layer without labels */}
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager_nolabels/{z}/{x}/{y}{r}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
            />
            {/* Labels layer on top - only hide during "playing" mode if setting is enabled */}
            {!(mode === "playing" && mapSettings.hideLabels) && (
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png"
                maxZoom={18}
                subdomains={["a", "b", "c", "d"]}
                opacity={0.55}
              />
            )}
          </>
        )}

        {/* Offline: Use simple world outline */}
        <OfflineBasemap show={!online} />

        {/* Only render GeoJSON when map is ready AND features are loaded */}
        {/* Key includes mode to re-render when entering complete/setup modes (for tooltips) */}
        {mapReady && features.length > 0 && (
          <GeoJSON
            key={`hover-${mapSettings.disableHover}-mode-${mode}`}
            ref={layerRef}
            data={featureCollection}
            style={styleFunction}
            onEachFeature={onEachFeature}
            pointToLayer={pointToLayer}
            renderer={canvasRenderer.current || undefined}
          />
        )}
      </MapContainer>
    </div>
  );
}

// Memoize component to prevent re-renders when parent updates unrelated state
// Only re-render when features, attempts, or onFeatureClick actually change
export const QuizMap = memo(QuizMapComponent, (prevProps, nextProps) => {
  // Custom comparison: only re-render if these actually changed
  const featuresEqual =
    prevProps.features.length === nextProps.features.length &&
    prevProps.features === nextProps.features; // Reference equality (features array should be stable from store)

  const attemptsEqual =
    prevProps.attempts.length === nextProps.attempts.length &&
    prevProps.attempts === nextProps.attempts; // Reference equality

  const callbackEqual = prevProps.onFeatureClick === nextProps.onFeatureClick;

  const labelsEqual = prevProps.showCompletionLabels === nextProps.showCompletionLabels;

  const shouldNotUpdate = featuresEqual && attemptsEqual && callbackEqual && labelsEqual;

  return shouldNotUpdate; // Return true to prevent re-render
});
